import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react';

export type DragStart = { x: number; y: number };
export type ResizeStart = { x: number; y: number; width: number; height: number };

type UseDraggableResizableOptions = {
  getDragStart: (event: ReactMouseEvent) => DragStart;
  getResizeStart: (event: ReactMouseEvent) => ResizeStart;
  onDragMove: (event: MouseEvent, dragStart: DragStart) => void;
  onResizeMove: (event: MouseEvent, resizeStart: ResizeStart) => void;
  onDragStart?: (event: ReactMouseEvent) => void;
  onResizeStart?: (event: ReactMouseEvent) => void;
  onDragEnd?: () => void;
  onResizeEnd?: () => void;
  isDragDisabled?: boolean;
  isResizeDisabled?: boolean;
};

const useDraggableResizable = ({
  getDragStart,
  getResizeStart,
  onDragMove,
  onResizeMove,
  onDragStart,
  onResizeStart,
  onDragEnd,
  onResizeEnd,
  isDragDisabled = false,
  isResizeDisabled = false
}: UseDraggableResizableOptions) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState<DragStart | null>(null);
  const [resizeStart, setResizeStart] = useState<ResizeStart | null>(null);

  const handleDragMouseDown = (event: ReactMouseEvent) => {
    if (isDragDisabled) return;
    onDragStart?.(event);
    setIsDragging(true);
    setDragStart(getDragStart(event));
  };

  const handleResizeMouseDown = (event: ReactMouseEvent) => {
    if (isResizeDisabled) return;
    onResizeStart?.(event);
    setIsResizing(true);
    setResizeStart(getResizeStart(event));
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (isDragging && dragStart) {
        onDragMove(event, dragStart);
      }
      if (isResizing && resizeStart) {
        onResizeMove(event, resizeStart);
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        onDragEnd?.();
      }
      if (isResizing) {
        setIsResizing(false);
        onResizeEnd?.();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, resizeStart, onDragMove, onResizeMove, onDragEnd, onResizeEnd]);

  return {
    isDragging,
    isResizing,
    handleDragMouseDown,
    handleResizeMouseDown
  };
};

export default useDraggableResizable;
